import type { Property, PropertyMatch, Requirement } from "../types";

export const HIGH_MATCH_THRESHOLD = 80;

export const WEIGHTS = {
  location: 30,
  budget: 25,
  purpose: 10,
  propertyType: 10,
  rooms: 10,
  facilities: 10,
  timeline: 5
};

function normalize(value: unknown) {
  return String(value || "").trim().toLowerCase();
}

function toArray(value: unknown) {
  if (Array.isArray(value)) return value.map(String);
  return String(value || "").split(",").map((item) => item.trim()).filter(Boolean);
}

function splitWords(value: string) {
  return normalize(value).split(/[\s,/.-]+/).filter((word) => word.length > 2);
}

function propertyPrice(property: Property) {
  return Number(property.promotionPrice || property.price || property.originalPrice || 0);
}

function locationScore(requirement: Requirement, property: Property) {
  const wanted = [
    requirement.location,
    ...toArray(requirement.preferredAreas),
    ...toArray(requirement.nearBts),
    ...toArray(requirement.nearMrt),
    ...toArray(requirement.nearAirportLink),
    ...toArray(requirement.nearAirport)
  ].map(normalize).filter(Boolean);
  const actual = [
    property.location,
    property.nearBts,
    property.nearMrt,
    property.nearAirportLink,
    property.nearAirport
  ].map(normalize).filter(Boolean);

  if (!wanted.length || !actual.length) return 1;
  if (wanted.some((target) => actual.some((item) => item === target || item.includes(target) || target.includes(item)))) return 1;

  const actualWords = new Set(actual.flatMap(splitWords));
  return wanted.some((target) => splitWords(target).some((word) => actualWords.has(word))) ? 0.55 : 0;
}

function budgetScore(requirement: Requirement, property: Property) {
  const price = propertyPrice(property);
  let min = Number(requirement.budgetMin || 0);
  let max = Number(requirement.budgetMax || 0);
  if (!price || (!min && !max)) return 1;
  if (!min) min = 0;
  if (!max) max = price;
  if (price >= min && price <= max) return 1;
  const range = Math.max(max - min, max * 0.15, 1);
  return price < min ? Math.max(0, 1 - (min - price) / range) : Math.max(0, 1 - (price - max) / range);
}

function purposeScore(requirement: Requirement, property: Property) {
  const wanted = normalize(requirement.purpose);
  const actual = normalize(property.purpose);
  if (!wanted || !actual) return 1;
  if (wanted === actual) return 1;
  if (wanted === "buy" && (actual === "sales" || actual === "sale")) return 1;
  if (actual === "buy" && (wanted === "sales" || wanted === "sale")) return 1;
  return 0;
}

function typeScore(requirement: Requirement, property: Property) {
  const wanted = normalize(requirement.propertyType);
  const actual = normalize(property.type);
  if (!wanted || !actual) return 1;
  return wanted === actual ? 1 : 0;
}

function ratioScore(wanted: number, actual: number) {
  if (!wanted || !actual) return 1;
  return actual >= wanted ? 1 : Math.max(0, actual / wanted);
}

function roomScore(requirement: Requirement, property: Property) {
  const lines = [
    ratioScore(Number(requirement.bedrooms || 0), Number(property.bedrooms || 0)),
    ratioScore(Number(requirement.bathrooms || 0), Number(property.bathrooms || 0)),
    ratioScore(Number(requirement.livingRooms || 0), Number(property.livingRooms || 0)),
    ratioScore(Number(requirement.kitchens || 0), Number(property.kitchens || 0)),
    ratioScore(Number(requirement.otherRooms || 0), Number(property.otherRooms || 0)),
    ratioScore(Number(requirement.size || 0), Number(property.size || 0))
  ];
  return lines.reduce((sum, score) => sum + score, 0) / lines.length;
}

function facilitiesScore(requirement: Requirement, property: Property) {
  const wanted = toArray(requirement.facilities);
  if (requirement.petFriendly) wanted.push("Pet Friendly");
  if (requirement.transitAccess) wanted.push("BTS/MRT Access");
  if (requirement.pool) wanted.push("Pool");
  if (requirement.fitness) wanted.push("Fitness");
  if (requirement.coWorkingSpace) wanted.push("Co-working space");
  if (requirement.garden) wanted.push("Garden");
  if (Number(requirement.parkingCars || 0) > 0) wanted.push("Parking");

  const actual = toArray(property.facilities);
  if (property.petFriendly) actual.push("Pet Friendly");
  if (property.nearBts || property.nearMrt) actual.push("BTS/MRT Access");
  if (property.pool) actual.push("Pool");
  if (property.fitness) actual.push("Fitness");
  if (property.coWorkingSpace) actual.push("Co-working space");
  if (property.garden) actual.push("Garden");
  if (Number(property.parkingCars || 0) > 0) actual.push("Parking");

  const normalizedWanted = wanted.map(normalize).filter(Boolean);
  const normalizedActual = actual.map(normalize).filter(Boolean);
  if (!normalizedWanted.length) return 1;
  if (!normalizedActual.length) return 0;
  const matched = normalizedWanted.filter((item) => normalizedActual.some((facility) => facility === item || facility.includes(item) || item.includes(facility)));
  return matched.length / normalizedWanted.length;
}

function timelineScore(requirement: Requirement, property: Property) {
  const wanted = normalize(requirement.timeline);
  const actual = normalize(property.timeline);
  if (!wanted || !actual) return 1;
  if (actual.includes("ready") || actual.includes("available now")) return 1;
  if (wanted.includes("month") && actual.includes("month")) return 0.85;
  return wanted === actual ? 1 : 0.5;
}

function scoreLine(label: string, value: number, weight: number) {
  return {
    label,
    raw: Number(value.toFixed(3)),
    weight,
    weighted: Number((value * weight).toFixed(2))
  };
}

export function calculateMatchScore(requirement: Requirement, property: Property) {
  const breakdown = [
    scoreLine("Location", locationScore(requirement, property), WEIGHTS.location),
    scoreLine("Budget", budgetScore(requirement, property), WEIGHTS.budget),
    scoreLine("Purpose", purposeScore(requirement, property), WEIGHTS.purpose),
    scoreLine("Type", typeScore(requirement, property), WEIGHTS.propertyType),
    scoreLine("Rooms", roomScore(requirement, property), WEIGHTS.rooms),
    scoreLine("Facilities", facilitiesScore(requirement, property), WEIGHTS.facilities),
    scoreLine("Timeline", timelineScore(requirement, property), WEIGHTS.timeline)
  ];
  const preciseScore = Number(breakdown.reduce((sum, line) => sum + line.weighted, 0).toFixed(2));
  const missing = breakdown.filter((line) => line.raw < 0.7).map((line) => `${line.label} needs review`);
  const explanation = breakdown
    .filter((line) => line.raw >= 0.7)
    .map((line) => `${line.label} matched ${Math.round(line.raw * 100)}%`);
  return {
    score: Math.round(preciseScore),
    preciseScore,
    breakdown,
    matchStatus: preciseScore >= HIGH_MATCH_THRESHOLD ? "High match" : "Needs review",
    matchExplanation: explanation,
    missingRequirements: missing
  };
}

export function rankProperties(requirement: Requirement | undefined, properties: Property[], limit = 10): PropertyMatch[] {
  if (!requirement) return [];
  return properties
    .map((property) => {
      const match = calculateMatchScore(requirement, property);
      return {
        ...property,
        matchScore: match.score,
        preciseMatchScore: match.preciseScore,
        matchBreakdown: match.breakdown,
        matchStatus: match.matchStatus,
        matchExplanation: match.matchExplanation,
        missingRequirements: match.missingRequirements
      };
    })
    .sort((a, b) => b.preciseMatchScore - a.preciseMatchScore || a.name.localeCompare(b.name))
    .slice(0, limit);
}
